import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/help"
          className="mb-6 flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to help
        </Link>

        <div className="mb-8">
          <h1 className="text-2xl font-bold">Privacy Policy</h1>
          <p className="mt-1 text-[var(--color-muted)]">Last updated: February 2025</p>
        </div>

        <div className="space-y-6 text-[var(--color-text)]">
          <section>
            <h2 className="mb-2 text-lg font-semibold">Overview</h2>
            <p className="text-[var(--color-muted)]">
              Bar Guide ("we", "our", or "us") operates the barguide.ai website and Bar Guide
              mobile applications. This page informs you of our policies regarding the
              collection, use, and disclosure of personal information when you use our service.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Information We Collect</h2>
            <p className="mb-2 text-[var(--color-muted)]">We collect the following types of information:</p>
            <ul className="list-disc space-y-1 pl-5 text-[var(--color-muted)]">
              <li>
                <strong>Account Information:</strong> When you sign in, we collect your email
                address and display name from your authentication provider (Google or Apple).
              </li>
              <li>
                <strong>Location Data:</strong> With your permission, we use your device's
                location to show bars and specials near you. This data is not stored on our
                servers.
              </li>
              <li>
                <strong>Usage Data:</strong> We collect anonymous analytics about how you use
                the app to improve our service.
              </li>
              <li>
                <strong>Preferences:</strong> Your favorites and hidden bars are stored locally
                on your device and synced to your account if you're signed in.
              </li>
              <li>
                <strong>Bar Submissions:</strong> If you submit a bar to be added to our
                service, we collect the bar information you provide along with your user ID and
                submission timestamp. Submissions are limited to 5 per day.
              </li>
              <li>
                <strong>Bar Ownership Claims:</strong> If you claim ownership of a bar, we
                collect your claim request and may compare your email domain against the bar's
                website domain for verification purposes.
              </li>
              <li>
                <strong>Area Coverage Requests:</strong> If you request Bar Guide coverage in a
                new area, we collect the location coordinates and your user ID. Requests are
                limited to 3 per day.
              </li>
              <li>
                <strong>Feedback:</strong> If you submit feedback through our in-app form, your
                name (optional), email, and message are sent to our feedback processing service.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Third-Party Services</h2>
            <p className="mb-2 text-[var(--color-muted)]">We use the following third-party services:</p>
            <ul className="list-disc space-y-1 pl-5 text-[var(--color-muted)]">
              <li>
                <strong>Firebase Authentication:</strong> For secure sign-in via Google or
                Apple.
              </li>
              <li>
                <strong>Google Places API:</strong> To display bar information and enable
                location search.
              </li>
              <li>
                <strong>Firebase Analytics:</strong> To understand app usage patterns.
              </li>
              <li>
                <strong>Formspree:</strong> For processing feedback form submissions.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Cookies and Local Storage</h2>
            <p className="text-[var(--color-muted)]">
              We use browser local storage to save your theme preference, favorites, and
              hidden bars. We do not use tracking cookies for advertising purposes.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Data Security</h2>
            <p className="text-[var(--color-muted)]">
              We implement industry-standard security measures to protect your personal
              information. All data is transmitted over HTTPS and stored securely.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Your Rights</h2>
            <p className="text-[var(--color-muted)]">
              You can delete your account and all associated data at any time from the
              Settings page. You can also clear your local preferences by clearing your
              browser's local storage.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Children's Privacy</h2>
            <p className="text-[var(--color-muted)]">
              Bar Guide is not intended for use by children under the age of 13. We do not
              knowingly collect personal information from children under 13. If you are a
              parent or guardian and believe your child has provided us with personal
              information, please contact us so we can delete that information.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">California Privacy Rights</h2>
            <p className="text-[var(--color-muted)]">
              If you are a California resident, you have the right to request information
              about how we share certain categories of personal information. You may also
              request deletion of your personal information. To make such a request, please
              contact us at{' '}
              <a
                href="mailto:privacy@mouserlabs.com"
                className="text-accent-primary hover:underline"
              >
                privacy@mouserlabs.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Data Retention</h2>
            <p className="text-[var(--color-muted)]">
              We retain your account information for as long as your account is active. If
              you delete your account, we will delete your personal information within 30
              days, except where we are required to retain it for legal purposes. Anonymous
              analytics data may be retained indefinitely.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Changes to This Policy</h2>
            <p className="text-[var(--color-muted)]">
              We may update this privacy policy from time to time. We will notify you of any
              changes by posting the new policy on this page.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Contact Us</h2>
            <p className="text-[var(--color-muted)]">
              If you have any questions about this Privacy Policy, please contact us at{' '}
              <a
                href="mailto:privacy@mouserlabs.com"
                className="text-accent-primary hover:underline"
              >
                privacy@mouserlabs.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
